import { useMemo, useState } from "react";
import "./SalesCharts.css";

const DIMENSIONS = [
  { key: "route", label: "Route" },
  { key: "customer", label: "Customer" },
  { key: "vendor", label: "Vendor" },
  { key: "product_category", label: "Product category" },
  { key: "account_type", label: "Account type" },
  { key: "salesman", label: "Salesmen / office" },
];

const moneyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function numberOf(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function money(value, showPositiveSign = false) {
  const number = numberOf(value);
  return `${showPositiveSign && number > 0 ? "+" : ""}${moneyFormatter.format(number)}`;
}

function percent(value) {
  if (value === null) return "No baseline";
  if (value === undefined || !Number.isFinite(Number(value))) return "—";
  const number = Number(value);
  return `${number > 0 ? "+" : ""}${number.toFixed(2)}%`;
}

function dimensionRows(report, key) {
  const rows = report?.breakdowns?.[key];
  return Array.isArray(rows) ? rows : [];
}

function DimensionSelect({ id, value, onChange, label }) {
  return (
    <div className="sales-charts-picker">
      <span aria-hidden="true">View by</span>
      <label className="sales-charts-sr-only" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {DIMENSIONS.map((dimension) => (
          <option key={dimension.key} value={dimension.key}>
            {dimension.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function EmptyChart({ title, detail }) {
  return (
    <div className="sales-charts-empty">
      <div className="sales-charts-empty-glyph" aria-hidden="true">
        <i />
        <i />
        <i />
      </div>
      <strong>{title}</strong>
      <span>{detail}</span>
    </div>
  );
}

function SalesLeaders({ report }) {
  const [dimension, setDimension] = useState("route");
  const { rows, total } = useMemo(() => {
    const ranked = dimensionRows(report, dimension)
      .filter((row) => numberOf(row.sales) !== 0)
      .sort((left, right) => numberOf(right.sales) - numberOf(left.sales));
    return { rows: ranked.slice(0, 6), total: ranked.length };
  }, [report, dimension]);
  const maxAbsolute = Math.max(
    ...rows.map((row) => Math.abs(numberOf(row.sales))),
    0
  );

  return (
    <section className="sales-charts-card" aria-label="Sales leaders">
      <div className="sales-charts-card-heading">
        <div>
          <span className="sales-charts-kicker">Current period</span>
          <h3>Sales leaders</h3>
          <p>Highest net sales for the selected dates.</p>
        </div>
        <DimensionSelect
          id="sales-charts-leaders-category"
          label="Sales leaders category"
          value={dimension}
          onChange={setDimension}
        />
      </div>

      {rows.length ? (
        <div className="sales-charts-rows" role="list">
          {rows.map((row, index) => {
            const value = numberOf(row.sales);
            return (
              <div
                className="sales-charts-row"
                role="listitem"
                key={row.key ?? `${row.id ?? row.name}-${index}`}
                aria-label={`${row.name || "Unassigned"}: ${money(value)} in sales`}
              >
                <div className="sales-charts-row-top">
                  <span
                    className="sales-charts-row-name"
                    title={row.name || "Unassigned"}
                  >
                    {row.name || "Unassigned"}
                  </span>
                  <strong
                    className={value < 0 ? "sales-charts-value--decline" : ""}
                  >
                    {money(value)}
                  </strong>
                </div>
                <div className="sales-charts-leader-track" aria-hidden="true">
                  <span
                    className={
                      value < 0
                        ? "sales-charts-leader-bar sales-charts-leader-bar--negative"
                        : "sales-charts-leader-bar"
                    }
                    style={{
                      width: `${Math.max(1.5, (Math.abs(value) / maxAbsolute) * 100)}%`,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyChart
          title="No sales to chart"
          detail="Choose another category or date range to see sales leaders."
        />
      )}
      <div className="sales-charts-card-foot">
        {rows.length
          ? `Showing ${rows.length} of ${total} entries by current sales`
          : "Sales include signed corrections and returns"}
      </div>
    </section>
  );
}

function GrowthAndDecline({ report }) {
  const [dimension, setDimension] = useState("product_category");
  const hasComparison = Boolean(report?.comparison);
  const { rows, total } = useMemo(() => {
    if (!hasComparison) return { rows: [], total: 0 };
    const ranked = dimensionRows(report, dimension)
      .filter((row) => numberOf(row.change_amount) !== 0)
      .sort(
        (left, right) =>
          Math.abs(numberOf(right.change_amount)) -
          Math.abs(numberOf(left.change_amount))
      );
    return { rows: ranked.slice(0, 6), total: ranked.length };
  }, [report, dimension, hasComparison]);
  const maxAbsolute = Math.max(
    ...rows.map((row) => Math.abs(numberOf(row.change_amount))),
    0
  );

  return (
    <section className="sales-charts-card" aria-label="Growth and decline">
      <div className="sales-charts-card-heading">
        <div>
          <span className="sales-charts-kicker">Period comparison</span>
          <h3>Growth and decline</h3>
          <p>Largest changes in net sales, measured in dollars.</p>
        </div>
        <DimensionSelect
          id="sales-charts-movement-category"
          label="Growth and decline category"
          value={dimension}
          onChange={setDimension}
        />
      </div>

      {rows.length ? (
        <>
          <div className="sales-charts-axis" aria-hidden="true">
            <span />
            <div className="sales-charts-axis-labels">
              <span>Decline</span>
              <span>0</span>
              <span>Growth</span>
            </div>
            <span />
          </div>
          <div className="sales-charts-movement-rows" role="list">
            {rows.map((row, index) => {
              const value = numberOf(row.change_amount);
              const direction = value < 0 ? "decline" : "growth";
              return (
                <div
                  className="sales-charts-movement-row"
                  role="listitem"
                  key={row.key ?? `${row.id ?? row.name}-${index}`}
                  aria-label={`${row.name || "Unassigned"}: ${money(value, true)} change, ${percent(row.change_percent)}`}
                >
                  <span
                    className="sales-charts-movement-name"
                    title={row.name || "Unassigned"}
                  >
                    {row.name || "Unassigned"}
                  </span>
                  <div
                    className="sales-charts-movement-plot"
                    aria-hidden="true"
                  >
                    <div className="sales-charts-movement-half sales-charts-movement-half--left">
                      {value < 0 && (
                        <span
                          className="sales-charts-movement-bar sales-charts-movement-bar--decline"
                          style={{
                            width: `${Math.max(1.5, (Math.abs(value) / maxAbsolute) * 100)}%`,
                          }}
                        />
                      )}
                    </div>
                    <div className="sales-charts-movement-half sales-charts-movement-half--right">
                      {value > 0 && (
                        <span
                          className="sales-charts-movement-bar sales-charts-movement-bar--growth"
                          style={{
                            width: `${Math.max(1.5, (value / maxAbsolute) * 100)}%`,
                          }}
                        />
                      )}
                    </div>
                  </div>
                  <div
                    className={`sales-charts-movement-value sales-charts-movement-value--${direction}`}
                  >
                    <strong>{money(value, true)}</strong>
                    <span>{percent(row.change_percent)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <EmptyChart
          title={hasComparison ? "No changes to chart" : "Select a comparison"}
          detail={
            hasComparison
              ? "Sales are unchanged in this category for the two periods."
              : "Choose a comparison above to see where sales grew or declined."
          }
        />
      )}
      <div className="sales-charts-card-foot">
        {rows.length
          ? `Showing ${rows.length} of ${total} entries by absolute dollar change`
          : "Growth and decline use the selected comparison period"}
      </div>
    </section>
  );
}

export default function SalesCharts({ report }) {
  return (
    <section className="sales-charts" aria-label="Sales charts">
      <div className="sales-analytics-section-head">
        <div>
          <span className="sales-analytics-eyebrow">Visual breakdown</span>
          <h2>Where sales are moving</h2>
        </div>
        <span className="sales-analytics-section-aside">
          Explore each category at a glance
        </span>
      </div>
      <div className="sales-charts-grid">
        <SalesLeaders report={report} />
        <GrowthAndDecline report={report} />
      </div>
    </section>
  );
}
